// @flow
import React from 'react';
import _ from 'lodash';
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import { connect } from 'react-redux';

import { Row, Col } from 'grab-ui';
import { Form } from 'semantic-grab-ui';

import SegPWidget from 'components/form/trident-campaigns/SegPWidget';
import SegPInsightsWidget from 'components/form/trident-campaigns/SegPInsightsWidget';
import { getSegmentName, getSegmentNamespace } from 'components/form/trident-campaigns/constants/utils';
import { targetUserTypes } from 'data/trident-types';
import Toastr from 'utils/toastr-util';
import { TRIDENT_OWNERS } from 'omega-rtc/constants/ucd';
import FormInputWithError from 'omega/components/form/FormInputWithError';
import { convertTridentActionToHedwigChannel, hedwigChannelToTridentBulkAction } from 'data/trident-campaigns';
import { getTemplate, getFeatureFlagConfig } from 'actions/newTridentExtra';
import { hasJustSuccess, hasJustFail } from 'utils/request-state-utils';
import { SubAction } from 'omega-rtc/types/trident-campaigns';
import { getNewActionSendPushConstraints } from 'components/form/trident-campaigns/constraints';
import NewActionSendPushNotification from 'components/form/trident-campaigns/NewActionSendPushNotification';
import { doSegmentValidation, SegmentParams } from 'utils/segp-utils';
import { getActionErrorDisplay, removeNullFields } from 'omega-rtc/utils/ucd-utils';

import './EnumerationActionPage.scss';

type Props = {
  country: Object;
  targetUserType: string;
  campaign: Object;
  countries: Object;
  formData: Object;
  handleFormDataChange: Function;
  getTemplate: Function,
  templates: Object,
  fetchTemplateRequest: RequestStates;
  validateFormRef: Function,
  error: any,
  countryPercentageWarningThreshold: number,
  featureFlagConfig: any,
  getFeatureFlagConfig: Function,
};

interface State {
  lastSelectedSegment: string;
  countryBreakDownAvailable: boolean;
  differentCountryPercent: number;
  segmentSize: number,
}

const DEFAULT_ACTION_NODE_ID = 3;
const DEFAULT_SUB_ACTION_INDEX = 1;

export class EnumerationActionPage extends React.Component<Props, State> {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.props.validateFormRef(this.validateForm);
    this.state = {
      lastSelectedSegment: _.get(props.formData, 'segmentName', ''),
      countryBreakDownAvailable: false,
      differentCountryPercent: 0, // percentage of users in the segment that are not in campaign's country
      segmentSize: 0,
    };

    this.actionForm = {};
  }

  componentDidMount() {
    this.props.getFeatureFlagConfig();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const templateID = _.get(this.props.formData, 'actions[0].meta.messagingPayload.templateID', '');
    const updatedActions = nextProps.formData.actions;

    if (
      hasJustSuccess(
        this.props.fetchTemplateRequest,
        nextProps.fetchTemplateRequest,
      )
    ) {
      const template = _.get(nextProps, `templates[${templateID}]`);
      if (template) {
        const hedwigChannel = _.get(template, 'templateType');
        _.set(updatedActions[0], 'type', hedwigChannelToTridentBulkAction[hedwigChannel]);
        this.props.handleFormDataChange({ actions: updatedActions });
      }
    }

    if (
      hasJustFail(
        this.props.fetchTemplateRequest,
        nextProps.fetchTemplateRequest,
      )
    ) {
      const errorMessage = _.get(nextProps, 'templates.errorMessage', '');
      _.set(updatedActions[0], 'error.templateID', errorMessage);
      this.props.handleFormDataChange({ actions: updatedActions });
      Toastr.error(errorMessage);
    }
  }

  validateForm = () => {
    const { formData } = this.props;
    // Validate Owner
    let ownerSectionErrors = [];
    const owner = _.get(formData, 'owner');
    if (_.isEmpty(owner)) {
      ownerSectionErrors = ["Owner can't be blank"];
    }
    if (owner === 'quest') {
      if (_.isEmpty(_.get(formData, 'ownerReferenceID', ''))) {
        ownerSectionErrors = [...ownerSectionErrors, "Owner Reference ID can't be blank"];
      }
    }

    // Validate Segment
    const segmentErrors = this.validateSelectedSegment();

    // Validate Action
    let actionFormRef = this.actionForm;
    if (_.isFunction(actionFormRef.getWrappedInstance)) {
      actionFormRef = actionFormRef.getWrappedInstance();
    }
    let actionErrors = [];
    if (_.isFunction(actionFormRef.getFormErrors)) {
      actionErrors = actionFormRef.getFormErrors();
    }

    const errors = {
      ownerErrors: ownerSectionErrors,
      actionErrors,
      segmentErrors,
    };
    return errors;
  }

  validateSelectedSegment = (): Array<String> => {
    const params: SegmentParams = {
      segmentName: _.get(this.props.formData, 'segmentName'),
      countryPercentageWarningThreshold: this.props.countryPercentageWarningThreshold,
      featureFlagConfig: this.props.featureFlagConfig,
      targetUserType: this.props.targetUserType,
      countryID: _.get(this.props.country, 'countryID'),
      campaignID: _.get(this.props.campaign, 'mamCampaignID'),
      differentCountryPercent: this.state.differentCountryPercent,
      countryBreakDownAvailable: this.state.countryBreakDownAvailable,
      segmentSize: this.state.segmentSize,
    };
    const error = doSegmentValidation(params);
    return _.isEmpty(error) ? [] : [error];
  };

  handleSegPOnChange = (val) => {
    this.setState({
      segmentVal: val,
    });
    this.handleInputChange(null, { name: 'segmentName', value: val });
  };

  onSegmentDataLoaded = (data) => {
    const segmentSize = _.get(data, 'segmentSize', 0);
    if (_.isEmpty(data) || segmentSize === 0) {
      this.setState({
        differentCountryPercent: 0,
        segmentSize: 0,
      });
      return;
    }

    // get campaign country
    const campaignCountryID = _.get(this.props, 'country.countryID');
    const countryName = _.get(
      _.find(this.props.countries, c => c.countryID === campaignCountryID),
      'name',
    );

    // count how many users in the segment are NOT in the campaign country
    const countryBreakdown = _.get(data, 'pax:country.attributes', []);
    const diffCountries = _.filter(countryBreakdown, x => x.name !== countryName);
    const diffCountryUserCount = _.sum(
      _.map(diffCountries, c => _.get(c, 'value', 0))
    );

    this.setState({
      differentCountryPercent: _.round((diffCountryUserCount / segmentSize) * 100.0, 2),
      countryBreakDownAvailable: !_.isEmpty(countryBreakdown),
      segmentSize,
    });
  };

  handleOwnerSelection = (event, data) => {
    const owner = _.get(data, 'value', '');
    this.props.handleFormDataChange({ owner });
  };

  handleInputChange = (event: any, { name, value }: { name: string, value: string }) => {
    const newFormData = this.props.formData;
    _.set(newFormData, name, value);
    this.props.handleFormDataChange({ ...newFormData });
  };

  onSubActionChange = (meta: Object) => {
    const updatedActions: Array<SubAction> = [
      {
        type: _.get(this.props.formData, 'actions[0].type'),
        meta: removeNullFields(meta),
      },
    ];
    this.props.handleFormDataChange({ actions: updatedActions });
  };

  constructTridentNodeStruct = () => {
    return {
      nodes: {
        [DEFAULT_ACTION_NODE_ID]: {
          type: 'actions',
          data: {
            [DEFAULT_SUB_ACTION_INDEX]: {
              type: _.get(this.props.formData, 'actions[0].type', ''),
              meta: _.get(this.props.formData, 'actions[0].meta', {}),
            },
          },
        },
      },
    };
  };

  render() {
    const {
      targetUserType,
      campaign,
      formData,
      error,
    } = this.props;
    const segment = _.get(formData, 'segmentName', '');
    const lastSelectedSegment = _.get(this.state, 'lastSelectedSegment');
    const segmentName = getSegmentName(segment);
    const segmentNamespace = getSegmentNamespace(segment);
    const showSegPInsightWidget = (
      targetUserType === targetUserTypes.pax
                                    || targetUserType === targetUserTypes.moveITPax
    )
                                  && !_.isEmpty(segmentName)
                                  && (lastSelectedSegment === segment);

    // Hedwig
    const commsChannels = _.get(campaign, 'channels', ['']);
    const hedwigChannels = _.map(commsChannels, channel => ((channel === 'pci') ? 'pushinbox' : channel)).join(',');

    const actionNewSendPushConstraints = getNewActionSendPushConstraints(DEFAULT_ACTION_NODE_ID, DEFAULT_SUB_ACTION_INDEX);

    // Owners
    const selectedOwner = _.get(formData, 'owner', '');
    const ownerReferenceID = _.get(formData, 'ownerReferenceID', '');

    // handle showing errors.
    const ownerErrors = _.get(error, 'ownerErrors', []);
    const segmentErrors = _.get(error, 'segmentErrors', []);

    const tridentNodeStruct = this.constructTridentNodeStruct();
    const actionMetaStruct = _.get(tridentNodeStruct, `nodes[${DEFAULT_ACTION_NODE_ID}].data[${DEFAULT_SUB_ACTION_INDEX}].meta`, {});

    const actionType = _.get(tridentNodeStruct, `nodes[${DEFAULT_ACTION_NODE_ID}].data[${DEFAULT_SUB_ACTION_INDEX}].type`, {});
    const isMultipleChannels = hedwigChannels.includes(',');

    return (
      <React.Fragment>
        <Row className={'sub-section-row form-row'} style={{ margin: '0 0 40px 0', paddingTop: 0, border: 0 }}>
          <h3 style={{ fontWeight: 500 }}> Select Owner</h3>
          <div className={'panel'}>
            <Row>
              {!_.isEmpty(ownerErrors) &&
                <div className="ui negative message">
                  <div className="header"><span className="validation-icon" />
                    {_.map(ownerErrors, (error) => <p>{error}</p>)}
                  </div>
                </div>
              }
              <Col span={12}>
                <div>
                  <Form.Select
                    required
                    label="Owner"
                    id="Owner"
                    name="owner"
                    options={TRIDENT_OWNERS}
                    value={selectedOwner}
                    onChange={this.handleOwnerSelection}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  {selectedOwner === 'quest' &&
                    <FormInputWithError
                      name="ownerReferenceID"
                      {...this.props}
                    >
                      <label>Owner Reference ID <span className={'red-asterisk'}>*</span></label>
                      <Form.Input
                        required
                        name={'ownerReferenceID'}
                        value={ownerReferenceID}
                        onChange={this.handleInputChange}
                        // onBlur={this.props.handleControlBlur}  //TODO: Find out later
                      />
                    </FormInputWithError>
                  }
                </div>
              </Col>
            </Row>
          </div>
        </Row>
        <Row className={'sub-section-row'}>
          <h3 style={{ fontWeight: 500 }}>User Audience</h3>
          <h4 style={{ fontWeight: 450 }}>User Segment</h4>
          {!_.isEmpty(segmentErrors) &&
            <div className="ui negative message">
              <div className="header"><span className="validation-icon" />
                {_.map(segmentErrors, (error) => <p>{error}</p>)}
              </div>
            </div>
          }
          <div>
            <SegPWidget
              targetUserType={targetUserType}
              onChange={this.handleSegPOnChange}
              onSelect={(val) => {
                this.setState({
                  lastSelectedSegment: val,
                });
              }}
              segmentVal={segment}
              maxVisible={30}
              enabledSegmentNSFormat
            />
          </div>
          <div className={'create-new-one'}>
            No segment yet?&nbsp;
            <a href={`${window.Grab.config.SEGMENTATION_PLATFORM_URI}/segments/create`} target={'_blank'}>
              Create one
            </a>
          </div>
        </Row>
        {
          showSegPInsightWidget &&
          <Row className={'sub-section-row'}>
            <div>
              <SegPInsightsWidget
                segmentID={segmentName}
                namespace={segmentNamespace}
                onDataLoaded={this.onSegmentDataLoaded}
              />
            </div>
          </Row>
        }
        <Row className={'sub-section-row hedwig-border'}>
          <h3 style={{ fontWeight: 500, marginBottom: 0 }}>Communications</h3>
          <div style={{ fontSize: 12, marginBottom: 20 }}>
            Choose template for <span style={{ fontStyle: 'italic' }}>{hedwigChannels}</span> {isMultipleChannels ? 'channels' : 'channel'}
          </div>

          <h4 style={{ fontWeight: 450, marginBottom: 5 }}>Template</h4>
          {(isMultipleChannels && !_.isEmpty(actionType)) &&
            <div style={{ fontSize: 12, marginBottom: 5, marginTop: -5 }}>
              Selected template channel: <span
                style={{ fontWeight: 600 }}
              >{convertTridentActionToHedwigChannel(actionType)} </span>
            </div>
          }
          {!_.isEmpty(_.get(error, 'actionErrors')) &&
            getActionErrorDisplay(error.actionErrors)
          }
          <NewActionSendPushNotification
            model={tridentNodeStruct}
            actionType={hedwigChannels}
            actionMeta={actionMetaStruct}
            targetUserType={targetUserType}
            onChange={this.onSubActionChange}
            constraints={actionNewSendPushConstraints}
            ref={form => this.actionForm = form}
            currentNode={DEFAULT_ACTION_NODE_ID}
            subActionIndex={DEFAULT_SUB_ACTION_INDEX}
            error={_.get(error, 'actionErrors', {})}
          />
          <br />
          <div className={'create-new-one'} style={{ marginTop: '0' }}>
            No template yet?&nbsp;
            <a href={`${window.Grab.config.HEDWIG_DASH_URI}/templates/create`} target={'_blank'}>Create one</a>
          </div>
        </Row>
      </React.Fragment>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    templates: _.get(state, 'newTridents.templates', {}),
    fetchTemplateRequest: _.get(
      state.requests,
      'getTemplateTridentCampaignsRequest',
      {},
    ),
    countryPercentageWarningThreshold: _.get(state, 'newTridents.segPConfig.countryPercentageWarningThreshold', 5),
    featureFlagConfig: _.get(state, 'newTridents.featureFlagConfig', {}),
  };
}

export default connect(
  mapStateToProps,
  {
    getTemplate,
    getFeatureFlagConfig,
  },
  null,
  { forwardRef: true },
)(EnumerationActionPage);
